import { dateOf, esc, monthOf } from './sbchart.ts';

type J = Record<string, any>;
const finite = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const day = (x: unknown): x is number => finite(x) && Number.isSafeInteger(x) && x >= 0;
const percent = (x: number) => x > 0 && x < 0.01 ? '<0.01%' : x > 99.99 && x < 100 ? '>99.99%' : `${Number(x.toFixed(2))}%`;
const amount = (x: unknown) => finite(x) ? `${x.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 元` : '未记录';

/** 旧 taxCut 已按 0.01 个百分点取整；不能把旧值 100 当成精确全免。 */
export function taxCard(g: J): { value: string; note: string; precision: string } {
  const p = g.taxPolicy ?? g.ops?.taxPolicy;
  const exact = finite(p?.factor) && p.factor >= 0 && p.factor <= 1;
  const cut = exact ? (1 - p.factor) * 100 : g.taxCut ?? g.ops?.taxCut;
  if (!finite(cut) || cut < 0 || cut > 100) return { value: '比例未记录', note: '交易税、个人所得税、公司利润税、城市配套费', precision: '当前减免与征收标准比例未记录。' };
  const approx = exact ? '' : '约 ';
  return {
    value: `减免${approx}${percent(cut)}`,
    note: `按各自标准${approx}${percent(exact ? p.factor * 100 : Math.max(0, 100 - cut))}征收`,
    precision: exact ? '征收比例相对于各税费自己的标准，不是统一的个人所得税率。' : '旧版比例按 0.01 个百分点披露；接近 100% 时不能据此确认精确全免。征收比例相对于各自标准，不是统一的个人所得税率。',
  };
}

/** 仅读同次 gov 查询的日期；不拿可能已前进的城市顶栏补历史日期。 */
export function taxSchedule(g: J): { asOf: number | null; rule: 'annual' | 'monthly' | 'disabled' | null; next: number | null; observation: number | null } {
  const p = g.taxPolicy ?? g.ops?.taxPolicy;
  const asOf = [p?.asOf, g.day, g.transport?.day].find(day) ?? null;
  const raw = p?.decisionRule;
  const legacy = g.ops?.taxRule ?? g.taxRule;
  const rule = p?.enabled === false || raw === 'disabled' ? 'disabled' : raw === 'annual' || raw === 'monthly' ? raw
    : legacy === '按年度预算调整，每月记录税收' ? 'annual' : legacy === '每月调整' ? 'monthly' : null;
  const nextAt = (period: number) => asOf == null ? null : Math.max(0, Math.floor((asOf - 7) / period) + 1) * period + 7;
  return { asOf, rule, next: rule === 'disabled' || rule == null ? null : day(p?.nextDecisionDay) && (asOf == null || p.nextDecisionDay > asOf) ? p.nextDecisionDay : nextAt(rule === 'annual' ? 360 : 30),
    observation: rule === 'disabled' || rule == null ? null : nextAt(30) };
}

const TAXES = [
  { key: 'sales', name: '交易税', tags: [52], basis: '按成交适用的交易税标准', timing: '随实际交易收取' },
  { key: 'income', name: '个人所得税', tags: [50], basis: '有免税额与分段税率，按合格收入计算', timing: '每月初按上月收入结算' },
  { key: 'profit', name: '公司利润税', tags: [51], basis: '按适用公司的正利润税基，实际可支付才收', timing: '每月初按上月利润结算' },
  { key: 'permit', name: '城市配套费', tags: [26], basis: '按施工许可适用面积与标准计算', timing: '领取适用施工许可时收取' },
] as const;
/** 持有税:引擎披露了才列(第 254 位置上的城不在减免里,照原来四项)。 */
const HOLDING = { key: 'holding', name: '房屋持有税', tags: [11], basis: '维修和持有费先买当天的维修工料，修完剩下的交国库', timing: '每天按房子面积交' } as const;
const taxList = (p: J) => Array.isArray(p?.taxes) && p.taxes.some((x: J) => x?.key === 'holding') ? [...TAXES, HOLDING] : [...TAXES];
/** 受年度减免的有几项(四项或连持有税五项)。 */
export const taxKinds = (g: J): string => taxList(g?.taxPolicy ?? g?.ops?.taxPolicy).length === 5 ? '五项' : '四项';

/** 只读已有完整月账。密集分类缺列是未知；稀疏 lastIn 仅在有完整月证据时补零。 */
export function taxReceipts(g: J): { known: boolean; closingDay: number | null; values: (number | null)[]; mixedTax: number | null } {
  const b = g.book, p = g.taxPolicy ?? g.ops?.taxPolicy;
  const dense = Array.isArray(b?.monthsIn) && b.monthsIn.length ? b.monthsIn[b.monthsIn.length - 1] : null;
  const known = p?.lastMonthKnown === false ? false : Array.isArray(dense) || Array.isArray(b?.flow) && b.flow.length > 0 && Array.isArray(b?.lastIn);
  const { asOf } = taxSchedule(g);
  const closingDay = !known ? null : day(p?.lastMonthDay) ? p.lastMonthDay : asOf != null && asOf >= 30 ? Math.floor(asOf / 30) * 30 : null;
  const sumTags = (tags: readonly number[]): number | null => {
    if (!known) return null;
    if (Array.isArray(dense)) return tags.every((k) => finite(dense[k])) ? tags.reduce((sum, k) => sum + dense[k], 0) : null;
    let sum = 0;
    for (const r of b.lastIn) if (tags.includes(r.k)) { if (!finite(r.amt)) return null; sum += r.amt; }
    return sum;
  };
  return { known, closingDay, values: taxList(p).map(({ tags }) => sumTags(tags)), mixedTax: sumTags([12]) };
}

export function taxOverviewHTML(g: J): string {
  const card = taxCard(g), schedule = taxSchedule(g), receipts = taxReceipts(g);
  const p = g.taxPolicy ?? g.ops?.taxPolicy;
  const period = receipts.closingDay != null ? `${monthOf(receipts.closingDay - 1)} · 结账日 ${dateOf(receipts.closingDay)}` : receipts.known ? '最近已结账月 · 日期未记录' : '尚无可读取的完整月';
  const rule = schedule.rule === 'annual' ? '每年评估一次减免，每月记录税收；年内沿用当前比例，不是每天重新决定。'
    : schedule.rule === 'monthly' ? '此城使用每月评估减免的规则。' : schedule.rule === 'disabled' ? '此城未启用这项减税决策。' : '该版本未披露减税评估周期，不能确定下次日期。';
  const date = schedule.asOf == null ? '查询日期未记录' : `数据截至 ${dateOf(schedule.asOf)}`;
  const next = schedule.next == null ? '下次减免评估日期未记录' : `下次计划评估：${dateOf(schedule.next)}（评估后比例也可能不变）`;
  const observation = schedule.observation == null ? '' : `；下次月度税收记录：${dateOf(schedule.observation)}`;
  return `<p><b>${esc(card.value)} · ${esc(card.note)}</b></p><p class="meta">${esc(card.precision)}</p>` +
    `<p class="meta">最近完整月：${esc(period)}。</p>` + taxList(p).map((t, i) => {
      const disclosed = Array.isArray(p?.taxes) ? p.taxes.find((x: J) => x.key === t.key) : null;
      const received = receipts.values[i];
      // Tax 混私人土地契税，Fee 混拆除费：不能冒充四项受减免税费的单独实收。
      const cash = i === 0 ? `<p><b>代收交易税实缴：${esc(amount(received))}</b></p><p><b>混合税款分类：${esc(amount(receipts.mixedTax))}</b></p><p class="meta">交易税完整金额无法单独分离；混合税款含直接交易税、私人土地契税等。</p>`
        : i === 3 ? `<p><b>规费分类合计：${esc(amount(received))}</b></p><p class="meta">配套费无法单独分离；规费还含拆除费等。</p>` : `<p><b>最近完整月实收：${esc(amount(received))}</b></p>`;
      const basis = typeof disclosed?.standardText === 'string' ? disclosed.standardText : t.basis;
      return `<section data-tax-kind="${t.key}"><h4>${t.name}</h4>${cash}<p class="meta">${esc(basis)}；${esc(t.timing)}。</p></section>`;
    }).join('') + '<p class="meta">仅列国库实际到账，不是应收、标准税收能力或本月尚未结账金额；零项保留。</p>' +
    `<p class="meta">${esc(date)}。${esc(rule)} ${esc(next + observation)}。</p>` +
    (schedule.rule === 'annual' ? '<p class="meta"><b>年度规则说明：</b>编预算时按既有余款、近一年其他净收入和估计的标准税收能力设定减免。其他收入与余款足够时，可能连续多年保持高减免；减免最高 100%，不会变成负税继续返款。这是规则说明，不是对本城上次决定的原因反推。</p>' : '') +
    (taxList(p).length === 5 ? '<p class="meta">减免统一作用于上述五项税费（连房屋持有税）；私人土地转让契税、卖地、租金、利息、医保缴费等另有规则。这里没有上次预算决策输入，不用今天余额倒推当时原因；实际决定见下方记录。</p>'
      : '<p class="meta">减免只统一作用于上述四项税费；房屋持有税、私人土地转让契税、卖地、租金、利息等另有规则。这里没有上次预算决策输入，不用今天余额倒推当时原因；实际决定见下方记录。</p>') +
    '<p class="meta"><a data-tab="govt|fin">看财政实际收入与支出 →</a> · <a data-tab="govt|plan">看资金预留和增员条件 →</a> · <a data-tab="govt|ops">看学校、医院等运营与缺口 →</a></p>';
}

export function tenderReserveLabel(): string { return '待签公共工程 · 尚未支付的预留承诺'; }
